import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import ReportExporter from '../components/ReportExporter';
import SearchableSelect from '../components/SearchableSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Filter, FileText, Edit, Trash2, Eye, MessageCircle, Mail, Send, Users, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP_NUMBER = '918983353140';

const LOAN_TYPES = [
  "Housing Loan", "Personal Loan", "Mortgage Loan", "Business Loan",
  "Car Loan", "Loan Take Over", "LAP", "Poultry Loan"
];

// Document requirements by loan type
const LOAN_DOCUMENTS = {
  "Housing Loan": ["आधार कार्ड", "पॅन कार्ड", "बँक स्टेटमेंट (1 वर्ष)", "सॅलरी स्लिप / ITR (3 वर्षे)", "प्रॉपर्टी दस्तऐवज", "पासपोर्ट फोटो (2)", "NOC"],
  "Personal Loan": ["आधार कार्ड", "पॅन कार्ड", "बँक स्टेटमेंट (6 महिने)", "सॅलरी स्लिप (3 महिने)", "पासपोर्ट फोटो (2)", "कंपनी ID"],
  "Mortgage Loan": ["आधार कार्ड", "पॅन कार्ड", "बँक स्टेटमेंट (1 वर्ष)", "मालमत्ता दस्तऐवज (7/12, खरेदीखत)", "ITR / सॅलरी स्लिप", "NOC", "मालमत्ता मूल्यांकन"],
  "Business Loan": ["आधार कार्ड", "पॅन कार्ड", "व्यवसाय नोंदणी", "GST नोंदणी", "बँक स्टेटमेंट (1 वर्ष)", "ITR (3 वर्षे)"],
  "Car Loan": ["आधार कार्ड", "पॅन कार्ड", "बँक स्टेटमेंट (6 महिने)", "सॅलरी स्लिप / ITR", "वाहन कोटेशन", "पासपोर्ट फोटो"],
  "default": ["आधार कार्ड", "पॅन कार्ड", "बँक स्टेटमेंट", "उत्पन्न पुरावा", "पासपोर्ट फोटो"]
};

const LOAN_STAGES = [
  "Application", "Document Verification", "Pending with Customer", 
  "Visit Done", "Logged with Bank", "Pending with Banker", "Bank Submission",
  "Sanctioned", "Disbursed", "Rejected", "Closed"
];

const stageColors = {
  'Application': 'bg-blue-100 text-blue-700 border-blue-200',
  'Document Verification': 'bg-amber-100 text-amber-700 border-amber-200',
  'Pending with Customer': 'bg-orange-100 text-orange-700 border-orange-200',
  'Visit Done': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Logged with Bank': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Pending with Banker': 'bg-violet-100 text-violet-700 border-violet-200',
  'Bank Submission': 'bg-purple-100 text-purple-700 border-purple-200',
  'Sanctioned': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Disbursed': 'bg-green-100 text-green-700 border-green-200',
  'Rejected': 'bg-red-100 text-red-700 border-red-200',
  'Closed': 'bg-slate-100 text-slate-600 border-slate-200'
};

const LoanFilesPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loanFiles, setLoanFiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bankAgents, setBankAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStages, setSelectedStages] = useState([...LOAN_STAGES]); // All selected by default
  const [selectedTypes, setSelectedTypes] = useState([...LOAN_TYPES]); // All selected by default
  const [selectedBanks, setSelectedBanks] = useState([]); // Will be populated after fetch
  const [periodFilter, setPeriodFilter] = useState('all');
  const _today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(_today.getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(_today.getMonth() / 3) + 1);
  const [selectedPeriodYear, setSelectedPeriodYear] = useState(_today.getFullYear());
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const periodYears = [_today.getFullYear() - 2, _today.getFullYear() - 1, _today.getFullYear(), _today.getFullYear() + 1];
  const [showStageFilter, setShowStageFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [showBankFilter, setShowBankFilter] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    loan_type: '',
    loan_amount: '',
    bank_agent_id: '',
    bank_agent_name: '',
    bank_agent_email: '',
    bank_agent_phone: '',
    assigned_employee_id: '',
    assigned_employee_name: '',
    stage: 'Application',
    remarks: ''
  });
  const [editFormData, setEditFormData] = useState({
    customer_name: '',
    customer_phone: '',
    loan_type: '',
    loan_amount: '',
    bank_agent_id: '',
    bank_agent_name: '',
    bank_agent_email: '',
    bank_agent_phone: '',
    assigned_employee_id: '',
    assigned_employee_name: '',
    stage: 'Application',
    remarks: ''
  });

  // Calculate date range based on period filter
  // NOTE: We build YYYY-MM-DD strings manually to avoid the timezone bug where
  // new Date(y, m, 1).toISOString() converts to UTC, shifting IST dates by ~5.5h
  // and returning the previous day (e.g., 1 May IST -> 30 Apr UTC).
  const ymd = (y, m, d) => `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const getDateRange = (period) => {
    const today = new Date();
    let start, end;
    
    switch(period) {
      case 'today':
        start = end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = ymd(weekStart.getFullYear(), weekStart.getMonth() + 1, weekStart.getDate());
        end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      }
      case 'month': {
        const m = selectedMonth; // 1-12
        const y = selectedPeriodYear;
        const lastDay = new Date(y, m, 0).getDate();
        start = ymd(y, m, 1);
        end = ymd(y, m, lastDay);
        break;
      }
      case 'quarter': {
        const qStartMonth = (selectedQuarter - 1) * 3 + 1; // 1-12
        const qEndMonth = qStartMonth + 2;
        const y = selectedPeriodYear;
        const lastDay = new Date(y, qEndMonth, 0).getDate();
        start = ymd(y, qStartMonth, 1);
        end = ymd(y, qEndMonth, lastDay);
        break;
      }
      case 'year':
        start = ymd(selectedPeriodYear, 1, 1);
        end = ymd(selectedPeriodYear, 12, 31);
        break;
      default:
        return null;
    }
    return { start, end };
  };

  // Filter loan files by period
  const getFilteredByPeriod = (files) => {
    if (periodFilter === 'all') return files;
    
    const range = getDateRange(periodFilter);
    if (!range) return files;
    
    return files.filter(f => {
      const fileDate = f.created_at?.split('T')[0] || f.application_date;
      if (!fileDate) return true;
      return fileDate >= range.start && fileDate <= range.end;
    });
  };

  // Toggle stage selection
  const toggleStage = (stage) => {
    setSelectedStages(prev => {
      if (prev.includes(stage)) {
        return prev.filter(s => s !== stage);
      } else {
        return [...prev, stage];
      }
    });
  };

  // Select all stages
  const selectAllStages = () => {
    setSelectedStages([...LOAN_STAGES]);
  };

  // Select only active stages (not closed/rejected)
  const selectActiveStages = () => {
    setSelectedStages(LOAN_STAGES.filter(s => s !== 'Closed' && s !== 'Rejected'));
  };

  // Clear all stages
  const clearAllStages = () => {
    setSelectedStages([]);
  };

  // Toggle type selection
  const toggleType = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Select all types
  const selectAllTypes = () => {
    setSelectedTypes([...LOAN_TYPES]);
  };

  // Clear all types
  const clearAllTypes = () => {
    setSelectedTypes([]);
  };

  // Toggle bank selection
  const toggleBank = (bank) => {
    setSelectedBanks(prev => prev.includes(bank) ? prev.filter(b => b !== bank) : [...prev, bank]);
  };

  // Get unique bank names from loan files
  const getUniqueBanks = () => {
    const banks = new Set();
    loanFiles.forEach(f => {
      if (f.bank_agent_name) banks.add(f.bank_agent_name);
    });
    return Array.from(banks).sort();
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Initialize selected banks after data is loaded
  useEffect(() => {
    if (loanFiles.length > 0 && selectedBanks.length === 0) {
      setSelectedBanks(getUniqueBanks());
    }
  }, [loanFiles]);

  const fetchData = async () => {
    try {
      const [filesRes, customersRes, employeesRes, agentsRes] = await Promise.all([
        axios.get(`${API}/loan-files`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/bank-agents`)
      ]);
      setLoanFiles(filesRes.data);
      setCustomers(customersRes.data);
      setEmployees(employeesRes.data);
      setBankAgents(agentsRes.data);
    } catch (error) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        loan_type: customer.loan_type
      }));
    }
  };

  const handleAgentSelect = (agentId) => {
    const agent = bankAgents.find(a => a.id === agentId);
    if (agent) {
      setFormData(prev => ({
        ...prev,
        bank_agent_id: agent.id,
        bank_agent_name: agent.name
      }));
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setFormData(prev => ({
        ...prev,
        assigned_employee_id: employee.id,
        assigned_employee_name: employee.name
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/loan-files`, {
        ...formData,
        loan_amount: parseFloat(formData.loan_amount)
      });
      toast.success('Loan file created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating loan file');
    }
  };

  const handleUpdateStage = async (fileId, newStage) => {
    try {
      await axios.put(`${API}/loan-files/${fileId}`, { stage: newStage });
      toast.success('Stage updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Error updating stage');
    }
  };

  const handleUpdateBankName = async (fileId, bankName) => {
    try {
      await axios.put(`${API}/loan-files/${fileId}`, { bank_agent_name: bankName });
      toast.success('Bank name updated');
      fetchData();
    } catch (error) {
      toast.error('Error updating bank name');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this loan file?')) return;
    try {
      await axios.delete(`${API}/loan-files/${fileId}`);
      toast.success('Loan file deleted');
      fetchData();
    } catch (error) {
      toast.error('Error deleting loan file');
    }
  };

  const handleEditClick = (file) => {
    setEditingFile(file);
    setEditFormData({
      customer_name: file.customer_name || '',
      customer_phone: file.customer_phone || '',
      loan_type: file.loan_type || '',
      loan_amount: file.loan_amount?.toString() || '',
      bank_agent_id: file.bank_agent_id || '',
      bank_agent_name: file.bank_agent_name || '',
      bank_agent_email: file.bank_agent_email || '',
      bank_agent_phone: file.bank_agent_phone || '',
      assigned_employee_id: file.assigned_employee_id || '',
      assigned_employee_name: file.assigned_employee_name || '',
      stage: file.stage || 'Application',
      remarks: file.remarks || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleEditAgentSelect = (agentId) => {
    if (agentId === "none") {
      setEditFormData(prev => ({
        ...prev,
        bank_agent_id: '',
        bank_agent_name: '',
        bank_agent_email: '',
        bank_agent_phone: ''
      }));
      return;
    }
    const agent = bankAgents.find(a => a.id === agentId);
    if (agent) {
      setEditFormData(prev => ({
        ...prev,
        bank_agent_id: agent.id,
        bank_agent_name: agent.name,
        bank_agent_email: agent.email || '',
        bank_agent_phone: agent.phone || ''
      }));
    }
  };

  const handleEditEmployeeSelect = (employeeId) => {
    if (employeeId === "none") {
      setEditFormData(prev => ({
        ...prev,
        assigned_employee_id: '',
        assigned_employee_name: ''
      }));
      return;
    }
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setEditFormData(prev => ({
        ...prev,
        assigned_employee_id: employee.id,
        assigned_employee_name: employee.name
      }));
    }
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`${API}/loan-files/${editingFile.id}`, {
        ...editFormData,
        loan_amount: parseFloat(editFormData.loan_amount) || 0
      });
      toast.success('Loan file updated successfully');
      setIsEditDialogOpen(false);
      setEditingFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating loan file');
    }
  };

  // WhatsApp - Customer Follow-up (with pending documents from Remarks)
  const sendWhatsAppFollowUp = (file) => {
    const phone = file.customer_phone?.replace(/\D/g, '') || '';
    if (!phone) {
      toast.error('Customer phone number not available');
      return;
    }
    
    // Get pending documents from remarks
    const pendingDocs = file.remarks ? `\n\n📋 Pending Documents:\n${file.remarks}` : '';
    
    const message = `नमस्कार ${file.customer_name},

तुमच्या ${file.loan_type} अर्जाबद्दल (File: ${file.file_number}) आम्ही follow-up करत आहोत.${pendingDocs}

कृपया pending कागदपत्रे लवकरात लवकर पाठवा.

कागदपत्रे WhatsApp वर पाठवा: https://wa.me/${WHATSAPP_NUMBER}

धन्यवाद!
Atharva Finance Solutions
📞 +91 74100 01036`;
    
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('WhatsApp opened - Send करा');
  };

  // WhatsApp - Document List (Remarks + Default list)
  const sendWhatsAppDocuments = (file) => {
    const phone = file.customer_phone?.replace(/\D/g, '') || '';
    if (!phone) {
      toast.error('Customer phone number not available');
      return;
    }
    
    // Use remarks if available, otherwise use default document list
    let docList;
    if (file.remarks) {
      docList = file.remarks;
    } else {
      const docs = LOAN_DOCUMENTS[file.loan_type] || LOAN_DOCUMENTS['default'];
      docList = docs.map(d => `• ${d}`).join('\n');
    }
    
    const message = `नमस्कार ${file.customer_name},

तुमच्या ${file.loan_type} (File: ${file.file_number}) साठी खालील कागदपत्रे pending आहेत:

${docList}

कृपया सर्व कागदपत्रे खालील WhatsApp नंबरवर पाठवा:
📱 https://wa.me/${WHATSAPP_NUMBER}

धन्यवाद!
Atharva Finance Solutions
📞 +91 74100 01036`;
    
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('WhatsApp opened - Send करा');
  };

  // WhatsApp - Banker Follow-up
  const sendWhatsAppBanker = (file) => {
    // First check loan file, then bank_agents
    let bankerPhone = file.bank_agent_phone?.replace(/\D/g, '') || '';
    if (!bankerPhone) {
      bankerPhone = bankAgents.find(a => a.id === file.bank_agent_id)?.phone?.replace(/\D/g, '') || '';
    }
    if (!bankerPhone) {
      toast.error('Banker phone number not available - Please add in Edit');
      return;
    }
    
    const bankerName = file.bank_agent_name || 'Sir/Madam';
    const message = `नमस्कार ${bankerName},

Loan File Reminder:
• Customer: ${file.customer_name}
• File No: ${file.file_number}
• Loan Type: ${file.loan_type}
• Amount: ₹${file.loan_amount?.toLocaleString('en-IN')}

कृपया या फाईलवर action घ्या.

धन्यवाद!
Atharva Finance Solutions`;
    
    const whatsappUrl = `https://wa.me/91${bankerPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('WhatsApp opened - Send करा');
  };

  // Email - Banker Assignment
  const sendEmailBanker = (file) => {
    const bankerEmail = file.bank_agent_email || '';
    if (!bankerEmail) {
      toast.error('Banker email not available - Please add in Edit');
      return;
    }
    
    const subject = `New Loan File Assigned - ${file.file_number}`;
    const body = `Dear Sir/Madam,

A new loan file has been assigned to you for processing.

LOAN DETAILS:
-----------------
File Number: ${file.file_number}
Customer Name: ${file.customer_name}
Customer Phone: ${file.customer_phone || 'N/A'}
Loan Type: ${file.loan_type}
Loan Amount: ₹${file.loan_amount?.toLocaleString('en-IN')}

Please review and process the application at your earliest convenience.

For any queries, please contact us at +91 74100 01036.

Best Regards,
Atharva Finance Solutions
info@atharvafs.com`;
    
    const mailtoUrl = `mailto:${bankerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
    toast.success('Email app opened - Send करा');
  };

  // Bulk WhatsApp - Send to all pending customers
  const sendBulkWhatsApp = () => {
    const pendingFiles = loanFiles.filter(f => f.stage === 'Pending with Customer' && f.customer_phone);
    
    if (pendingFiles.length === 0) {
      toast.error('No pending files with customer phone numbers');
      return;
    }
    
    // Save to localStorage for tracking
    const sentHistory = JSON.parse(localStorage.getItem('notificationHistory') || '[]');
    
    let opened = 0;
    pendingFiles.forEach((file, index) => {
      setTimeout(() => {
        const phone = file.customer_phone?.replace(/\D/g, '') || '';
        if (phone) {
          // Include pending documents from remarks
          const pendingDocs = file.remarks ? `\n\n📋 Pending Documents:\n${file.remarks}` : '';
          
          const message = `नमस्कार ${file.customer_name},

तुमच्या ${file.loan_type} अर्जाबद्दल (File: ${file.file_number}) आम्ही follow-up करत आहोत.${pendingDocs}

कृपया pending कागदपत्रे लवकरात लवकर पाठवा.

कागदपत्रे WhatsApp वर पाठवा: https://wa.me/${WHATSAPP_NUMBER}

धन्यवाद!
Atharva Finance Solutions
📞 +91 74100 01036`;
          
          const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
          opened++;
          
          // Save to history
          sentHistory.unshift({
            id: Date.now() + index,
            type: 'whatsapp',
            recipient: file.customer_name,
            phone: file.customer_phone,
            loan_file: file.file_number,
            message_type: 'Customer Follow-up',
            sent_at: new Date().toISOString()
          });
        }
      }, index * 1500); // 1.5 second delay between each
    });
    
    localStorage.setItem('notificationHistory', JSON.stringify(sentHistory.slice(0, 100)));
    toast.success(`${pendingFiles.length} WhatsApp windows opening - Send each message`);
  };

  // Bulk WhatsApp - Send to all pending bankers
  const sendBulkBankerWhatsApp = () => {
    // Filter files that are pending with banker AND have banker phone (from loan file or bank_agents)
    const pendingFiles = loanFiles.filter(f => {
      if (f.stage !== 'Pending with Banker') return false;
      // Check if banker phone exists in loan file or bank_agents
      const hasPhoneInFile = f.bank_agent_phone;
      const hasPhoneInAgents = f.bank_agent_id && bankAgents.find(a => a.id === f.bank_agent_id)?.phone;
      return hasPhoneInFile || hasPhoneInAgents;
    });
    
    if (pendingFiles.length === 0) {
      toast.error('No files with banker phone number. Please add banker phone in loan file.');
      return;
    }
    
    const sentHistory = JSON.parse(localStorage.getItem('notificationHistory') || '[]');
    
    let opened = 0;
    pendingFiles.forEach((file, index) => {
      setTimeout(() => {
        // First try loan file's banker phone, then bank_agents
        let bankerPhone = file.bank_agent_phone?.replace(/\D/g, '') || '';
        let bankerName = file.bank_agent_name || '';
        
        if (!bankerPhone && file.bank_agent_id) {
          const banker = bankAgents.find(a => a.id === file.bank_agent_id);
          bankerPhone = banker?.phone?.replace(/\D/g, '') || '';
          bankerName = banker?.name || bankerName;
        }
        
        if (bankerPhone) {
          const message = `नमस्कार ${bankerName || 'Sir/Madam'},

Loan File Reminder:
• Customer: ${file.customer_name}
• File No: ${file.file_number}
• Loan Type: ${file.loan_type}
• Amount: ₹${file.loan_amount?.toLocaleString('en-IN')}

कृपया या फाईलवर action घ्या.

धन्यवाद!
Atharva Finance Solutions
📞 +91 74100 01036`;
          
          const whatsappUrl = `https://wa.me/91${bankerPhone}?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
          opened++;
          
          sentHistory.unshift({
            id: Date.now() + index,
            type: 'whatsapp',
            recipient: bankerName || 'Banker',
            phone: bankerPhone,
            loan_file: file.file_number,
            message_type: 'Banker Follow-up',
            sent_at: new Date().toISOString()
          });
        }
      }, index * 1500);
    });
    
    localStorage.setItem('notificationHistory', JSON.stringify(sentHistory.slice(0, 100)));
    toast.success(`${pendingFiles.length} WhatsApp windows opening for bankers`);
  };

  // Get pending counts
  const pendingCustomerCount = loanFiles.filter(f => f.stage === 'Pending with Customer' && f.customer_phone).length;
  const pendingBankerCount = loanFiles.filter(f => {
    if (f.stage !== 'Pending with Banker') return false;
    return f.bank_agent_phone || (f.bank_agent_id && bankAgents.find(a => a.id === f.bank_agent_id)?.phone);
  }).length;
  const totalPendingBanker = loanFiles.filter(f => f.stage === 'Pending with Banker').length;

  const resetForm = () => {
    setFormData({
      customer_id: '',
      customer_name: '',
      loan_type: '',
      loan_amount: '',
      bank_agent_id: '',
      bank_agent_name: '',
      assigned_employee_id: '',
      assigned_employee_name: '',
      remarks: ''
    });
  };

  const filteredFiles = getFilteredByPeriod(loanFiles.filter(file => {
    const matchesSearch = file.file_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Stage filter - check if file's stage is in selected stages
    const matchesStage = selectedStages.includes(file.stage);
    
    // Type filter - check if file's type is in selected types
    const matchesType = selectedTypes.includes(file.loan_type);
    
    return matchesSearch && matchesStage && matchesType;
  }));

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="loan-files-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Loan Files</h1>
          <p className="text-slate-500 mt-1">Manage and track loan applications</p>
        </div>
        <div className="flex items-center gap-3">
          <ReportExporter type="loans" />
          <Button
            onClick={() => setIsDialogOpen(true)}
            data-testid="create-loan-file-btn"
            className="bg-brand-navy text-white hover:bg-brand-navy/90 shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Loan File
          </Button>
        </div>
      </div>

      {/* Period Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        {[
          { key: 'today', label: 'Today' },
          { key: 'week', label: 'This Week' },
          { key: 'month', label: 'This Month' },
          { key: 'quarter', label: 'This Quarter' },
          { key: 'year', label: 'This Year' },
          { key: 'all', label: 'All Time' }
        ].map(tab => (
          <Button
            key={tab.key}
            variant={periodFilter === tab.key ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriodFilter(tab.key)}
            className={periodFilter === tab.key ? 'bg-brand-navy text-white' : ''}
            data-testid={`loanfiles-period-${tab.key}`}
          >
            {tab.label}
          </Button>
        ))}
        {periodFilter === 'month' && (
          <>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {monthNames.map((n, i) => <SelectItem key={i+1} value={(i+1).toString()}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'quarter' && (
          <>
            <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
              <SelectTrigger className="w-[170px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'year' && (
          <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* Bulk Actions Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-green-50 to-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{pendingCustomerCount}</p>
                <p className="text-xs text-slate-500">Pending with Customer</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-violet-600">{pendingBankerCount}</p>
                <p className="text-xs text-slate-500">Pending with Banker</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={sendBulkWhatsApp}
                disabled={pendingCustomerCount === 0}
                className="text-green-600 border-green-300 hover:bg-green-50"
                data-testid="bulk-whatsapp-customer"
              >
                <Users className="h-4 w-4 mr-2" />
                Bulk WhatsApp - Customers ({pendingCustomerCount})
              </Button>
              <Button
                variant="outline"
                onClick={sendBulkBankerWhatsApp}
                disabled={pendingBankerCount === 0}
                className="text-violet-600 border-violet-300 hover:bg-violet-50"
                data-testid="bulk-whatsapp-banker"
              >
                <Users className="h-4 w-4 mr-2" />
                Bulk WhatsApp - Bankers ({pendingBankerCount})
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/notification-history')}
                className="text-slate-600 border-slate-300"
                data-testid="view-history"
              >
                <History className="h-4 w-4 mr-2" />
                History
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by file number or customer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-loan-files"
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Stage Checkbox Filter */}
          {showStageFilter && (
            <div className="border rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <Label className="font-semibold text-slate-700">Select Stages to Show:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={selectAllStages}>
                    All
                  </Button>
                  <Button size="sm" variant="outline" onClick={selectActiveStages}>
                    Active Only
                  </Button>
                  <Button size="sm" variant="outline" onClick={clearAllStages}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {LOAN_STAGES.map(stage => (
                  <label
                    key={stage}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                      selectedStages.includes(stage)
                        ? stageColors[stage] || 'bg-slate-100 border-slate-300'
                        : 'bg-white border-slate-200 opacity-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStages.includes(stage)}
                      onChange={() => toggleStage(stage)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-xs font-medium truncate">{stage}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Type Checkbox Filter */}
          {showTypeFilter && (
            <div className="border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-3">
                <Label className="font-semibold text-slate-700">Select Loan Types to Show:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={selectAllTypes}>
                    All
                  </Button>
                  <Button size="sm" variant="outline" onClick={clearAllTypes}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {LOAN_TYPES.map(type => (
                  <label
                    key={type}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                      selectedTypes.includes(type)
                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                        : 'bg-white border-slate-200 opacity-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={() => toggleType(type)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-xs font-medium truncate">{type}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">File No.</TableHead>
                <TableHead className="font-semibold text-slate-600">Customer</TableHead>
                <TableHead className="font-semibold text-slate-600">Contact No.</TableHead>
                <TableHead 
                  className="font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                  onClick={() => setShowTypeFilter(!showTypeFilter)}
                >
                  <div className="flex items-center gap-1">
                    Loan Type
                    <Filter className={`h-3 w-3 ${selectedTypes.length < LOAN_TYPES.length ? 'text-brand-navy' : 'text-slate-400'}`} />
                    {selectedTypes.length < LOAN_TYPES.length && (
                      <span className="text-xs text-brand-navy">({selectedTypes.length})</span>
                    )}
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600">Amount</TableHead>
                <TableHead 
                  className="font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                  onClick={() => setShowStageFilter(!showStageFilter)}
                >
                  <div className="flex items-center gap-1">
                    Stage
                    <Filter className={`h-3 w-3 ${selectedStages.length < LOAN_STAGES.length ? 'text-brand-navy' : 'text-slate-400'}`} />
                    {selectedStages.length < LOAN_STAGES.length && (
                      <span className="text-xs text-brand-navy">({selectedStages.length})</span>
                    )}
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600">
                  Assigned to Bank
                </TableHead>
                <TableHead className="font-semibold text-slate-600">Remarks</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFiles.map((file) => (
                <TableRow key={file.id} className="hover:bg-slate-50/50">
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap" data-testid={`loan-date-${file.id}`}>
                    {file.created_at ? new Date(file.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  </TableCell>
                  <TableCell className="font-mono text-sm font-medium text-brand-navy">
                    {file.file_number}
                  </TableCell>
                  <TableCell>{file.customer_name}</TableCell>
                  <TableCell className="text-sm">
                    {file.customer_phone ? (
                      <a href={`tel:${file.customer_phone}`} className="text-brand-navy hover:underline">
                        {file.customer_phone}
                      </a>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal">{file.loan_type}</Badge>
                  </TableCell>
                  <TableCell className="font-semibold">{formatCurrency(file.loan_amount)}</TableCell>
                  <TableCell>
                    <Select
                      value={file.stage}
                      onValueChange={(value) => handleUpdateStage(file.id, value)}
                    >
                      <SelectTrigger className={`w-[160px] h-8 text-xs ${stageColors[file.stage]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LOAN_STAGES.map(stage => (
                          <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      defaultValue={file.bank_agent_name || ''}
                      onBlur={(e) => {
                        if (e.target.value !== (file.bank_agent_name || '')) {
                          handleUpdateBankName(file.id, e.target.value);
                        }
                      }}
                      className="w-[140px] h-8 text-xs"
                      placeholder="Enter bank name"
                    />
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate text-slate-600 text-sm">
                    {file.remarks || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => { setSelectedFile(file); setIsViewDialogOpen(true); }}
                        data-testid={`view-loan-${file.id}`}
                        title="View Details & Send Messages"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEditClick(file)}
                        data-testid={`edit-loan-${file.id}`}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(file.id)}
                          data-testid={`delete-loan-${file.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredFiles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                    No loan files found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Create New Loan File</DialogTitle>
            <DialogDescription>Fill in the details to create a new loan application</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer Selection</Label>
              <div className="flex gap-2">
                <SearchableSelect
                  options={customers.map(c => ({ value: c.id, label: c.name, sublabel: c.phone }))}
                  value=""
                  onChange={handleCustomerSelect}
                  placeholder="Select from existing customers"
                  searchPlaceholder="Search customer by name or phone..."
                  emptyText="No customers found."
                  dataTestId="select-customer"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">Or enter customer name manually below</p>
            </div>
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                data-testid="input-customer-name"
                placeholder="Enter customer name manually"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact No.</Label>
              <Input
                value={formData.customer_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                data-testid="input-customer-phone"
                placeholder="Enter contact number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Loan Type</Label>
                <Select value={formData.loan_type} onValueChange={(v) => setFormData(prev => ({ ...prev, loan_type: v }))}>
                  <SelectTrigger data-testid="select-loan-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Loan Amount (₹)</Label>
                <Input
                  type="number"
                  value={formData.loan_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, loan_amount: e.target.value }))}
                  data-testid="input-loan-amount"
                  placeholder="Enter amount"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bank Agent</Label>
              <SearchableSelect
                options={bankAgents.map(a => ({ value: a.id, label: a.name, sublabel: a.bank_name }))}
                value=""
                onChange={handleAgentSelect}
                placeholder="Choose bank agent"
                searchPlaceholder="Search agent by name or bank..."
                emptyText="No bank agents found."
                dataTestId="select-bank-agent"
              />
            </div>
            <div className="space-y-2">
              <Label>Assign to Employee</Label>
              <SearchableSelect
                options={employees.map(emp => ({ value: emp.id, label: emp.name }))}
                value=""
                onChange={handleEmployeeSelect}
                placeholder="Choose employee"
                searchPlaceholder="Search employee..."
                emptyText="No employees found."
                dataTestId="select-employee"
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                data-testid="input-remarks"
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              data-testid="submit-loan-file-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!formData.customer_name || !formData.loan_type || !formData.loan_amount}
            >
              Create Loan File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Loan File Details</DialogTitle>
          </DialogHeader>
          {selectedFile && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">File Number</p>
                  <p className="font-mono font-semibold text-brand-navy">{selectedFile.file_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Stage</p>
                  <Badge className={stageColors[selectedFile.stage]}>{selectedFile.stage}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Customer</p>
                  <p className="font-medium">{selectedFile.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Loan Type</p>
                  <p className="font-medium">{selectedFile.loan_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Loan Amount</p>
                  <p className="font-semibold text-lg">{formatCurrency(selectedFile.loan_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Bank Agent</p>
                  <p className="font-medium">{selectedFile.bank_agent_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Assigned Employee</p>
                  <p className="font-medium">{selectedFile.assigned_employee_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Created</p>
                  <p className="font-medium">{new Date(selectedFile.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {selectedFile.remarks && (
                <div>
                  <p className="text-sm text-slate-500">Remarks</p>
                  <p className="text-slate-700">{selectedFile.remarks}</p>
                </div>
              )}
              
              {/* Quick Action Buttons */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-slate-600 mb-3">Quick Actions - Send Message</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendWhatsAppFollowUp(selectedFile)}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    data-testid="btn-whatsapp-followup"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    Follow-up + Remarks
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendWhatsAppDocuments(selectedFile)}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    data-testid="btn-whatsapp-docs"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Pending Docs
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendWhatsAppBanker(selectedFile)}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    data-testid="btn-whatsapp-banker"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    Banker WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendEmailBanker(selectedFile)}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    data-testid="btn-email-banker"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Banker Email
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Edit Loan File</DialogTitle>
            <DialogDescription>
              {editingFile && `File: ${editingFile.file_number} | Customer: ${editingFile.customer_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer Name</Label>
              <Input
                value={editFormData.customer_name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                data-testid="edit-customer-name"
                placeholder="Enter customer name"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact No.</Label>
              <Input
                value={editFormData.customer_phone}
                onChange={(e) => setEditFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                data-testid="edit-customer-phone"
                placeholder="Enter contact number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Loan Type</Label>
                <Select 
                  value={editFormData.loan_type} 
                  onValueChange={(v) => setEditFormData(prev => ({ ...prev, loan_type: v }))}
                >
                  <SelectTrigger data-testid="edit-loan-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Loan Amount (₹)</Label>
                <Input
                  type="number"
                  value={editFormData.loan_amount}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, loan_amount: e.target.value }))}
                  data-testid="edit-loan-amount"
                  placeholder="Enter amount"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select 
                value={editFormData.stage} 
                onValueChange={(v) => setEditFormData(prev => ({ ...prev, stage: v }))}
              >
                <SelectTrigger data-testid="edit-stage">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {LOAN_STAGES.map(stage => (
                    <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assigned to Bank</Label>
              <Input
                value={editFormData.bank_agent_name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, bank_agent_name: e.target.value }))}
                data-testid="edit-bank-name"
                placeholder="Enter bank name manually"
              />
            </div>
            <div className="space-y-2">
              <Label>Banker Email (for notifications)</Label>
              <Input
                type="email"
                value={editFormData.bank_agent_email}
                onChange={(e) => setEditFormData(prev => ({ ...prev, bank_agent_email: e.target.value }))}
                data-testid="edit-bank-email"
                placeholder="banker@bank.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Banker Mobile No. (for WhatsApp)</Label>
              <Input
                value={editFormData.bank_agent_phone}
                onChange={(e) => setEditFormData(prev => ({ ...prev, bank_agent_phone: e.target.value }))}
                data-testid="edit-bank-phone"
                placeholder="9876543210"
              />
            </div>
            <div className="space-y-2">
              <Label>Assign to Employee</Label>
              <Select value={editFormData.assigned_employee_id || "none"} onValueChange={handleEditEmployeeSelect}>
                <SelectTrigger data-testid="edit-employee">
                  <SelectValue placeholder="Choose employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={editFormData.remarks}
                onChange={(e) => setEditFormData(prev => ({ ...prev, remarks: e.target.value }))}
                data-testid="edit-remarks"
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleEditSubmit}
              data-testid="submit-edit-loan-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
            >
              Update Loan File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoanFilesPage;
